package com.corbettcode.allaboutrocks.pages.typesof

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HERO_IMAGE_MAX_WIDTH
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.ImageCreditStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.components.widgets.Figure
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

// Art is still to come. Captions describe the intended photo; the credit lines are placeholders
// rather than invented attributions, and need filling in with the actual source.
private const val CREDIT_TODO = "Photo: TODO — add attribution when the image lands"

@InitRoute
fun initTypesOfEpidote(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Types of Rocks"))
}

/** The settings that put calcium, aluminum, and iron together with silica and water. */
private val epidoteFormation = listOf(
    "Regional metamorphism" to "Burial and squeezing of basalt and other calcium-rich volcanic rock at moderate temperatures grows epidote through whole mountain belts. It is abundant enough at these conditions that geologists name the greenschist and epidote-amphibolite facies after the minerals that appear there."
    , "Hydrothermal alteration" to "Hot water working through volcanic rock breaks down the plagioclase feldspar and leaves epidote in its place — an alteration called saussuritization, which turns a grey andesite a dull pistachio green from the inside out."
    , "Veins and vugs" to "The same fluids carry the ingredients into open fractures and gas cavities, where epidote has room to grow as sharply terminated crystals alongside quartz, prehnite, and calcite. This is where collector specimens come from."
    , "Contact metamorphism and skarn" to "Where an intrusion cooks limestone or a calcium-rich sediment, epidote grows in the reaction zone, often with garnet and diopside, and often close to ore."
    , "Fissures in granite" to "Alpine-type clefts in granitic rock produce fine crystals in small numbers, which is the setting behind the classic Austrian material."
)

/** What to check when you think you have a piece. */
private val epidoteKeyCharacteristics = listOf(
    "Pistachio green" to "The color does most of the work. Epidote is yellow-green to olive, sometimes so dark it reads as black, and the particular yellowish cast has been called pistachio green for two centuries because nothing else describes it as well."
    , "Striated prisms" to "Crystals are elongated in one direction and grooved along their length, so a good specimen looks like a bundle of fluted columns. They are often flattened, and they end in a slanted termination rather than a point."
    , "Hardness" to "6–7 on the Mohs scale. It scratches glass, but it is brittle, and it will chip rather than take the abuse quartz shrugs off."
    , "One perfect cleavage" to "Epidote splits cleanly in a single direction across the length of the crystal. That splitting is the main reason it is awkward to cut, and it separates epidote from the green amphiboles, which cleave two ways at an angle."
    , "Heavy for a silicate" to "Specific gravity runs about 3.4, noticeably denser than quartz at 2.65. A fist-sized piece feels wrong in the hand in a way you learn to recognize."
    , "Strong pleochroism" to "Turn a translucent crystal in the light and the color shifts between green, yellow, and brown as the viewing angle changes."
    , "Luster and streak" to "Vitreous on a fresh face, with a colorless to greyish streak — a green mineral leaving a white streak rules out most of the copper minerals it might otherwise be confused with."
)

/** Epidote's relatives, and the rock most collectors actually meet it in. */
private val epidoteVarieties = listOf(
    "Clinozoisite" to "The same structure with little or no iron, which drops the color back to pale green, grey, or nearly colorless. Epidote and clinozoisite grade into one another as a continuous series, and only the iron content separates them."
    , "Zoisite" to "The orthorhombic counterpart of clinozoisite — the same chemistry in a different crystal system. Tanzanite and the pink thulite are its best-known varieties."
    , "Piemontite" to "Manganese in place of some of the iron, giving a striking red to reddish purple. Uncommon, and worth recognizing when it turns up."
    , "Allanite" to "An epidote-group mineral carrying rare earth elements, usually black or brown and often partly broken down by its own radioactivity. Common as small grains in granite."
    , "Unakite" to "Not a mineral but an altered granite — pink orthoclase feldspar mottled with green epidote and grey quartz. Named for the Unaka Range on the North Carolina and Tennessee line, spread widely by glaciers, and by far the most common way a rockhound ends up owning epidote."
    , "Epidosite" to "Rock made almost entirely of epidote and quartz, formed where hydrothermal fluids stripped a basalt of nearly everything else. It shows up as bright green patches and pods in old sea-floor volcanic rock."
)

/** Outside references, paired with what each one is actually good for. */
private val epidoteFurtherReading = listOf(
    Triple(
        "Wikipedia: Epidote"
        , "The general overview — chemistry, crystallography, the epidote group, and the classic localities, with sources worth following."
        , "https://en.wikipedia.org/wiki/Epidote"
    )
    , Triple(
        "Mindat: Epidote"
        , "Mineral data and a locality database running past eleven thousand entries, which is the practical way to find out whether epidote has been recorded near you."
        , "https://www.mindat.org/min-1389.html"
    )
    , Triple(
        "Geology.com: Epidote"
        , "Plain-language coverage of epidote as a rock-forming mineral and of the epidote group as a whole, with photographs of typical material."
        , "https://geology.com/minerals/epidote.shtml"
    )
    , Triple(
        "Minerals.net: Epidote"
        , "Physical properties and a survey of the varieties, handy when you are working out where a particular specimen sits in the group."
        , "https://www.minerals.net/mineral/epidote.aspx"
    )
    , Triple(
        "Wikipedia: Unakite"
        , "A closer look at the pink-and-green altered granite that most tumbled epidote comes from."
        , "https://en.wikipedia.org/wiki/Unakite"
    )
)

@Composable
private fun EpidoteSectionTitle(text: String) {
    SpanText(
        text
        , PageSectionTitleStyle.toModifier()
            .color(textColor())
    )
}

@Composable
private fun TypesOfEpidoteHeadline() {
    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Epidote"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun TypesOfEpidoteHero() {

    TypesOfEpidoteHeadline()

    // 1 column on mobile, 2 columns on md+ (image left, text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/typesof/epidote/the_types_of_epidote.png"
                    , alt = "types of epidote"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
                SpanText(
                    "created by ChatGPT"
                    , ImageCreditStyle.toModifier()
                )
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Epidote is a calcium aluminum iron silicate, and it is one of the most common minerals almost nobody can name. It grows wherever volcanic rock has been cooked, squeezed, or soaked in hot water, which covers a great deal of the West — so the green staining on a roadcut, the green flecks in a beach cobble, and the pink-and-green rock in a tumbler are all very often the same mineral.")
            }

            EpidoteSectionTitle("How It Forms")
            P {
                Text("Epidote needs calcium, aluminum, iron, silica, and water, all of which are sitting in ordinary basalt and andesite. Add moderate heat and pressure, or just hot circulating groundwater, and the feldspar and pyroxene in those rocks break down and epidote grows in their place. It forms across a wide band of conditions, which is exactly why it is everywhere:")
            }
        }
    }

    Column(
        HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Ul(PageListStyle.toAttrs()) {
            epidoteFormation.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        Figure(
            source = "/images/typesof/epidote/epidote_on_quartz.jpg"
            , alt = "Green epidote veining through weathered volcanic host rock"
            , caption = "Epidote running through altered volcanic rock — the green is what is left after hot water worked through the feldspar."
            , credit = CREDIT_TODO
        )
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        EpidoteSectionTitle("Key Characteristics")

        Ul(PageListStyle.toAttrs()) {
            epidoteKeyCharacteristics.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(": $description")
                }
            }
        }

        EpidoteSectionTitle("The Epidote Group and Unakite")
        P {
            Text("Epidote gives its name to a group of minerals built on the same framework, with different elements swapped into the same sites. Most of them look nothing alike:")
        }

        Ul(PageListStyle.toAttrs()) {
            epidoteVarieties.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        Figure(
            source = "/images/typesof/epidote/unakite_rock.webp"
            , alt = "Tumbled unakite pebbles showing pink feldspar and green epidote"
            , caption = "Tumbled unakite — pink orthoclase and green epidote in an altered granite. This is the form most collections have, whether or not the label says epidote."
            , credit = CREDIT_TODO
        )

        EpidoteSectionTitle("Telling It from Other Green Minerals")
        P {
            Text("Green is a crowded field, and epidote gets mistaken for most of it. Olivine is a cleaner bottle-green and grows as glassy grains in fresh basalt rather than as striated prisms. Actinolite and the other green amphiboles cleave in two directions that meet at an angle, while epidote cleaves perfectly in one. Serpentine is soft enough to scratch with a knife and feels greasy. Malachite is softer still, effervesces in acid, and leaves a green streak where epidote leaves a pale one. And green fluorite, which can look very close in a vug, is softer again and breaks along four cleavage directions into neat little octahedral chips.")
        }

        EpidoteSectionTitle("Where to Find It")
        P {
            Text("Epidote is common across the volcanic and metamorphic West — the Cascades, the Sierra, and the Rockies all carry it, and it turns up in altered basalt, in skarn near intrusions, and as green grains in beach and river gravel far from where it grew. Collector-quality crystals are a different matter and come from a short list of places: the Knappenwand in Austria's Untersulzbachtal set the standard in the nineteenth century, Green Monster Mountain on Prince of Wales Island in Alaska has produced the finest American material, and Pakistan has supplied much of what is on the market lately. Unakite is easiest of all to find, since glaciers spread it across the upper Midwest and it is sold by the bucket. As always, check the land status before you collect — BLM ground, national forest, private land, and state parks each have their own rules."
            )
        }

        EpidoteSectionTitle("Working With It")
        P {
            Text("Epidote is an awkward lapidary material in crystal form and an easy one in massive form. The crystals are brittle and split on that single perfect cleavage, so they chip on the wheel and are usually better left as specimens than cut — the transparent material that does get faceted is dark, and a stone of any size ends up nearly black. Massive epidote and epidosite cut and polish well, and unakite is about as forgiving as rock gets: it tumbles to a good polish, takes a cabochon cleanly, and is cheap enough to practice on. The one thing to watch in unakite is the feldspar, which is softer than the epidote and will undercut slightly if you rush the final stages."
            )
        }

        EpidoteSectionTitle("For More Information")

        Ul(PageListStyle.toAttrs()) {
            epidoteFurtherReading.forEach { (title, description, url) ->
                Li {
                    B { Link(url, title) }
                    Text(" — $description")
                }
            }
        }
    }
}

@Page("/types_of_rocks/epidote")
@Layout(".components.layouts.PageLayout")
@Composable
fun TypesOfEpidotePage() {
    TypesOfEpidoteHero()
}
